import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Typography,
  message,
  Space,
  Input,
  Alert,
  Modal,
  Tag,
  Select,
  Descriptions,
  Tooltip
} from 'antd';
import { ReloadOutlined, RollbackOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import devToolsService from '../services/devToolsService.ts';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface FulfilledOrder {
  id: number;
  order_number: string;
  customer_order_number: string | null;
  customer: string | null;
  order_date: string | null;
  status_id: number;
  status_name: string | null;
  reorder: boolean;
  fulfilled_rows: number;
  fulfilled_pieces: number;
  fulfilled_at: string | null;
}

interface PlanLine {
  cart_id: number;
  product_id: number;
  product: string;
  stock_id: number;
  stock_code: string | null;
  quantity: number;
  warehouse: string | null;
  udc: string | null;
  customs_document: string | null;
  action: string;
  ok: boolean;
}

interface RollbackPlan {
  order: {
    id: number;
    order_number: string;
    customer: string | null;
    reorder: boolean;
    status_id: number;
    status_name: string | null;
    fulfilled_at: string | null;
  };
  previous_status_id: number;
  previous_status_name: string | null;
  previous_status_source: 'history' | 'default' | 'manual';
  target_statuses: { id: number; name: string }[];
  lines: PlanLine[];
  total_pieces: number;
  out_movements_to_delete: number;
  stocks_to_revive: number;
  blocks: string[];
  warnings: string[];
  can_apply: boolean;
}

const fmtDate = (v: string | null, withTime = false) =>
  v ? dayjs(v).format(withTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY') : '—';

const RollbackEvasioneOrdine: React.FC = () => {
  const [orders, setOrders] = useState<FulfilledOrder[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const [plan, setPlan] = useState<RollbackPlan | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [targetStatusId, setTargetStatusId] = useState<number | undefined>(undefined);
  const [notes, setNotes] = useState('');
  const [applying, setApplying] = useState(false);

  const loadOrders = useCallback(async (term?: string) => {
    setLoading(true);
    try {
      const data = await devToolsService.getFulfilledOrders(term);
      setOrders(data?.items || []);
    } catch (error: any) {
      console.error('Errore caricamento ordini evasi:', error);
      message.error(error?.response?.data?.detail || 'Errore nel caricamento degli ordini evasi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const openPreview = async (orderId: number, statusId?: number) => {
    setPlanLoading(true);
    try {
      const data: RollbackPlan = await devToolsService.previewFulfillmentRollback(orderId, statusId);
      setPlan(data);
      setTargetStatusId(data.previous_status_id);
    } catch (error: any) {
      console.error('Errore anteprima rollback:', error);
      message.error(error?.response?.data?.detail || "Errore nell'anteprima del rollback");
    } finally {
      setPlanLoading(false);
    }
  };

  const closePreview = () => {
    setPlan(null);
    setNotes('');
    setTargetStatusId(undefined);
  };

  const handleTargetChange = (value: number) => {
    if (!plan) return;
    setTargetStatusId(value);
    openPreview(plan.order.id, value);
  };

  const handleApply = () => {
    if (!plan || !plan.can_apply) return;
    Modal.confirm({
      title: `Annullare l'evasione dell'ordine ${plan.order.order_number}?`,
      content: (
        <div>
          <p>
            L'ordine tornerà allo stato <b>{plan.previous_status_name}</b>.
            {plan.order.reorder
              ? ` Le ${plan.lines.length} righe di carrello evase verranno eliminate (nessun ricarico inventario).`
              : ` ${plan.total_pieces} pezzi verranno ricaricati sulle giacenze originali e ${plan.out_movements_to_delete} movimenti OUT eliminati.`}
          </p>
          <p>L'operazione è registrata nella Cronologia dell'ordine.</p>
        </div>
      ),
      okText: 'Conferma rollback',
      okButtonProps: { danger: true },
      cancelText: 'Annulla',
      onOk: async () => {
        setApplying(true);
        try {
          const result = await devToolsService.applyFulfillmentRollback(plan.order.id, {
            notes: notes.trim() || undefined,
            target_status_id: targetStatusId
          });
          message.success(result?.message || 'Rollback eseguito');
          closePreview();
          loadOrders(search);
        } catch (error: any) {
          console.error('Errore rollback:', error);
          message.error(error?.response?.data?.detail || "Errore durante il rollback dell'evasione");
        } finally {
          setApplying(false);
        }
      }
    });
  };

  const columns = [
    {
      title: 'N. Ordine',
      dataIndex: 'order_number',
      key: 'order_number',
      render: (v: string, r: FulfilledOrder) => (
        <Space>
          <Text strong>{v}</Text>
          {r.reorder && <Tag color="blue">STOCK</Tag>}
        </Space>
      )
    },
    { title: 'Rif. Cliente', dataIndex: 'customer_order_number', key: 'customer_order_number', render: (v: string | null) => v || '—' },
    { title: 'Cliente', dataIndex: 'customer', key: 'customer', render: (v: string | null) => v || '—' },
    { title: 'Data Ordine', dataIndex: 'order_date', key: 'order_date', render: (v: string | null) => fmtDate(v) },
    { title: 'Stato', dataIndex: 'status_name', key: 'status_name', render: (v: string | null) => <Tag color="purple">{v || '—'}</Tag> },
    { title: 'Righe Evase', dataIndex: 'fulfilled_rows', key: 'fulfilled_rows', align: 'right' as const },
    { title: 'Pezzi', dataIndex: 'fulfilled_pieces', key: 'fulfilled_pieces', align: 'right' as const },
    { title: 'Evaso il', dataIndex: 'fulfilled_at', key: 'fulfilled_at', render: (v: string | null) => fmtDate(v, true) },
    {
      title: 'Azioni',
      key: 'actions',
      render: (_: unknown, r: FulfilledOrder) => (
        <Button
          type="primary"
          danger
          size="small"
          icon={<RollbackOutlined />}
          loading={planLoading}
          onClick={() => openPreview(r.id)}
        >
          Anteprima Rollback
        </Button>
      )
    }
  ];

  const lineColumns = [
    { title: 'Prodotto', dataIndex: 'product', key: 'product' },
    {
      title: 'Giacenza',
      key: 'stock',
      render: (_: unknown, l: PlanLine) => (
        <span>#{l.stock_id}{l.stock_code ? ` (${l.stock_code})` : ''}</span>
      )
    },
    { title: 'Magazzino', dataIndex: 'warehouse', key: 'warehouse', render: (v: string | null) => v || '—' },
    { title: 'UDC', dataIndex: 'udc', key: 'udc', render: (v: string | null) => v || '—' },
    { title: 'Bolletta', dataIndex: 'customs_document', key: 'customs_document', render: (v: string | null) => v || '—' },
    { title: 'Q.tà', dataIndex: 'quantity', key: 'quantity', align: 'right' as const },
    {
      title: 'Effetto',
      dataIndex: 'action',
      key: 'action',
      render: (v: string, l: PlanLine) => (
        <Tooltip title={v}>
          <Tag color={l.ok ? 'green' : 'red'}>{l.ok ? 'OK' : 'BLOCCO'}</Tag> <Text type="secondary">{v}</Text>
        </Tooltip>
      )
    }
  ];

  return (
    <div>
      <Title level={3}>Rollback Evasione Ordine Cliente</Title>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Strumento riservato all'amministratore"
        description={
          <div>
            Annulla l'evasione di un ordine cliente e riporta ordine, carrello e inventario alla situazione
            precedente: le prenotazioni tornano attive, i pezzi vengono ricaricati sulle stesse giacenze,
            i contatori delle bollette vengono riallineati e i movimenti OUT dell'evasione eliminati.
            Per gli <b>ordini stock</b> vengono rimosse le sole righe di audit (nessun ricarico) e la
            copertura torna modificabile. Ogni rollback è registrato nella Cronologia dell'ordine.
          </div>
        }
      />

      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="Cerca per numero ordine, riferimento cliente o cliente"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={() => loadOrders(search)}
            style={{ width: 360 }}
            allowClear
          />
          <Button icon={<SearchOutlined />} onClick={() => loadOrders(search)}>Cerca</Button>
          <Button icon={<ReloadOutlined />} onClick={() => { setSearch(''); loadOrders(); }} loading={loading}>
            Ricarica
          </Button>
          <Text type="secondary">{orders.length} ordini in stato di evasione</Text>
        </Space>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={orders}
          loading={loading}
          size="small"
          pagination={{ pageSize: 20, showSizeChanger: true }}
        />
      </Card>

      <Modal
        open={!!plan}
        title={plan ? `Anteprima rollback — ordine ${plan.order.order_number}` : ''}
        onCancel={closePreview}
        width={1100}
        footer={[
          <Button key="cancel" onClick={closePreview}>Chiudi</Button>,
          <Button
            key="apply"
            type="primary"
            danger
            icon={<RollbackOutlined />}
            disabled={!plan?.can_apply}
            loading={applying}
            onClick={handleApply}
          >
            Esegui Rollback
          </Button>
        ]}
      >
        {plan && (
          <div>
            <Descriptions size="small" column={3} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Cliente">{plan.order.customer || '—'}</Descriptions.Item>
              <Descriptions.Item label="Tipo">{plan.order.reorder ? <Tag color="blue">ORDINE STOCK</Tag> : 'Ordine normale'}</Descriptions.Item>
              <Descriptions.Item label="Evaso il">{fmtDate(plan.order.fulfilled_at, true)}</Descriptions.Item>
              <Descriptions.Item label="Stato attuale"><Tag color="purple">{plan.order.status_name}</Tag></Descriptions.Item>
              <Descriptions.Item label="Stato dopo il rollback" span={2}>
                <Space>
                  <Select
                    size="small"
                    style={{ width: 220 }}
                    value={targetStatusId}
                    onChange={handleTargetChange}
                    loading={planLoading}
                    options={plan.target_statuses.map((s) => ({ value: s.id, label: s.name }))}
                  />
                  <Text type="secondary">
                    {plan.previous_status_source === 'history' && '(ricostruito dalla Cronologia)'}
                    {plan.previous_status_source === 'default' && '(non ricostruibile: valore di ripiego)'}
                    {plan.previous_status_source === 'manual' && '(scelto manualmente)'}
                  </Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Pezzi">{plan.total_pieces}</Descriptions.Item>
              <Descriptions.Item label="Movimenti OUT da eliminare">{plan.out_movements_to_delete}</Descriptions.Item>
              <Descriptions.Item label="Giacenze da riattivare">{plan.stocks_to_revive}</Descriptions.Item>
            </Descriptions>

            {plan.blocks.length > 0 && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 12 }}
                message="Rollback non applicabile"
                description={<ul style={{ margin: 0, paddingLeft: 18 }}>{plan.blocks.map((b, i) => <li key={i}>{b}</li>)}</ul>}
              />
            )}
            {plan.warnings.length > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="Avvisi"
                description={<ul style={{ margin: 0, paddingLeft: 18 }}>{plan.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
              />
            )}

            <Table
              rowKey="cart_id"
              columns={lineColumns}
              dataSource={plan.lines}
              size="small"
              pagination={plan.lines.length > 15 ? { pageSize: 15 } : false}
              style={{ marginBottom: 12 }}
            />

            <TextArea
              rows={2}
              placeholder="Motivo del rollback (facoltativo, registrato nella Cronologia e nelle note dell'ordine)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={400}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RollbackEvasioneOrdine;
